import { useState, useEffect } from 'react';
import { dashboard } from '../services/api';

export default function ReorderAlerts({ accountId }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(30);

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    dashboard.inventory(accountId).then(inv => {
      const flagged = inv
        .filter(item => item.days_of_stock !== null)
        .map(item => ({
          ...item,
          urgency: item.days_of_stock <= 7 ? 'critical'
            : item.days_of_stock <= 14 ? 'warning'
            : item.days_of_stock <= threshold ? 'low'
            : 'ok',
        }))
        .filter(item => item.urgency !== 'ok')
        .sort((a, b) => a.days_of_stock - b.days_of_stock);
      setAlerts(flagged);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [accountId, threshold]);

  const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '12px' };

  const urgencyConfig = {
    critical: { label: 'Critical', bg: '#FCEBEB', color: '#A32D2D', border: '#F5C2C2', emoji: '🔴' },
    warning: { label: 'Warning', bg: '#FAEEDA', color: '#854F0B', border: '#F5DFA8', emoji: '🟡' },
    low: { label: 'Low stock', bg: '#EEF2FF', color: '#3730A3', border: '#C7D2FE', emoji: '🔵' },
  };

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: '13px', fontWeight: '500' }}>Reorder Alerts</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#666' }}>
            Alert threshold:
            <select value={threshold} onChange={e => setThreshold(parseInt(e.target.value))}
              style={{ padding: '5px 8px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '12px' }}>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={45}>45 days</option>
              <option value={60}>60 days</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '1rem' }}>
          {['critical', 'warning', 'low'].map(u => {
            const count = alerts.filter(a => a.urgency === u).length;
            const cfg = urgencyConfig[u];
            return (
              <div key={u} style={{ background: cfg.bg, border: `0.5px solid ${cfg.border}`, borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', color: cfg.color, fontWeight: '500', marginBottom: '5px' }}>{cfg.emoji} {cfg.label}</div>
                <div style={{ fontSize: '28px', fontWeight: '500', color: cfg.color }}>{count}</div>
                <div style={{ fontSize: '11px', color: cfg.color, opacity: 0.8 }}>SKUs need attention</div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading alerts...</div>}

      {!loading && alerts.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '3rem', color: '#888' }}>
          🎉 All products have healthy stock levels! No reorder alerts.
        </div>
      )}

      {!loading && alerts.map(item => {
        const cfg = urgencyConfig[item.urgency];
        return (
          <div key={item.sku} style={{ ...card, border: `0.5px solid ${cfg.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{item.sku}</span>
                  <span style={{ background: cfg.bg, color: cfg.color, border: `0.5px solid ${cfg.border}`, fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '4px' }}>
                    {cfg.emoji} {cfg.label}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>{item.product_name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '24px', fontWeight: '500', color: cfg.color }}>{item.days_of_stock}d</div>
                <div style={{ fontSize: '11px', color: '#999' }}>days of stock left</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginTop: '1rem' }}>
              {[
                { label: 'Units available', value: item.units_available },
                { label: 'Units inbound', value: item.units_inbound || 0 },
                { label: 'Avg sold/day', value: parseFloat(item.avg_daily_units_7d || 0).toFixed(1) },
                { label: 'Reorder qty (30d)', value: Math.ceil(parseFloat(item.avg_daily_units_7d || 0) * 30) },
              ].map(m => (
                <div key={m.label} style={{ background: '#f5f4f0', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', color: '#888', marginBottom: '3px' }}>{m.label}</div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>{m.value}</div>
                </div>
              ))}
            </div>

            {item.urgency === 'critical' && (
              <div style={{ marginTop: '12px', background: cfg.bg, border: `0.5px solid ${cfg.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: cfg.color }}>
                ⚠️ <strong>Action required:</strong> At current sales velocity you will run out of stock in {item.days_of_stock} days. Place a reorder immediately!
              </div>
            )}
            {item.urgency === 'warning' && (
              <div style={{ marginTop: '12px', background: cfg.bg, border: `0.5px solid ${cfg.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: cfg.color }}>
                📦 <strong>Reorder soon:</strong> Stock running low. Consider placing an order in the next few days.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}